import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Avatar,
  AvatarGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Speed,
  Timeline,
  Assignment,
  Warning,
  CheckCircle,
  BugReport,
  TrendingUp,
  Group,
  Assessment
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import SprintTimelineView from '../Sprint/SprintTimelineView';

interface SprintMetricsCardProps {
  sprintId: string;
  sprint: any;
  onViewDetails?: (sprintId: string) => void;
}

const SprintMetricsCard: React.FC<SprintMetricsCardProps> = ({ 
  sprintId, 
  sprint,
  onViewDetails 
}) => {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [sprintDetails, setSprintDetails] = useState<any>(null);

  useEffect(() => {
    loadSprintMetrics();
  }, [sprintId]);

  const loadSprintMetrics = async () => {
    try {
      setLoading(true);
      const sprintMetrics = await apiService.getSprintMetrics(sprintId);
      setMetrics(sprintMetrics);
    } catch (error) {
      console.error('Error loading sprint metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSprintStatusColor = (state: string) => {
    switch (state) {
      case 'active':
        return 'success';
      case 'completed':
        return 'primary';
      case 'planning':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 80) return 'success.main';
    if (score >= 60) return 'warning.main';
    return 'error.main';
  };

  const getHealthScoreIcon = (score: number) => {
    if (score >= 80) return <CheckCircle />;
    if (score >= 60) return <Warning />;
    return <BugReport />;
  };

  if (!metrics) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>Loading metrics...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => onViewDetails?.(sprintId)}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }} noWrap>
              {sprint.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
            </Typography>
          </Box>
          <Chip
            label={sprint.state.toUpperCase()}
            color={getSprintStatusColor(sprint.state) as any}
            size="small"
            sx={{ ml: 1 }}
          />
        </Box>

        {/* Sprint Goal */}
        {sprint.goal && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
            "{sprint.goal}"
          </Typography>
        )}

        {/* Progress Bars */}
        <Box sx={{ mb: 2 }}>
          {/* Story Points Progress */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Story Points
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {metrics.completedStoryPoints}/{metrics.totalStoryPoints} ({metrics.progressPercentage}%)
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={metrics.progressPercentage} 
            sx={{ height: 8, borderRadius: 4, mb: 2 }}
          />

          {/* Time Progress */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Time Progress
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {metrics.elapsedDays}/{metrics.totalDays} days ({metrics.idealProgress}%)
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={metrics.idealProgress} 
            sx={{ height: 6, borderRadius: 3 }}
            color="secondary"
          />
        </Box>

        {/* Key Metrics */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" color="primary">
                {metrics.totalIssues}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Issues
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" color="success.main">
                {metrics.completedIssues}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Completed
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" color="warning.main">
                {metrics.activeIssues}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" color="error.main">
                {metrics.blockedIssues}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Blocked
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Health Score and Velocity */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Sprint Health Score">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getHealthScoreIcon(metrics.healthScore)}
                <Typography 
                  variant="body2" 
                  fontWeight="medium"
                  color={getHealthScoreColor(metrics.healthScore)}
                >
                  {metrics.healthScore}
                </Typography>
              </Box>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              Health
            </Typography>
          </Box>
          
          {metrics.velocity > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Speed sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="body2" fontWeight="medium">
                {Math.round(metrics.velocity)} SP/day
              </Typography>
            </Box>
          )}
        </Box>

        {/* Team Members */}
        {metrics.uniqueAssignees > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Team Members
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {metrics.uniqueAssignees}
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Tooltip title="View Timeline">
            <IconButton 
              size="small" 
              onClick={async (e) => {
                e.stopPropagation();
                if (!sprintDetails) {
                  try {
                    const details = await apiService.getSprint(sprintId);
                    setSprintDetails(details);
                  } catch (error) {
                    console.error('Error loading sprint details:', error);
                  }
                }
                setTimelineOpen(true);
              }}
              color="primary"
            >
              <Timeline />
            </IconButton>
          </Tooltip>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {/* Detailed Metrics */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Sprint Metrics
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Remaining Days"
                      secondary={metrics.remainingDays}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Projected Completion"
                      secondary={`${metrics.projectedCompletion} days`}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Status"
                      secondary={metrics.isOnTrack ? 'On Track' : metrics.isAtRisk ? 'At Risk' : 'Behind'}
                    />
                  </ListItem>
                </List>
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Epic Progress
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Total Epics"
                      secondary={metrics.totalEpics}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Completed"
                      secondary={metrics.completedEpics}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Completion Rate"
                      secondary={`${metrics.epicCompletionRate}%`}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>

            {/* Alerts */}
            {metrics.isAtRisk && (
              <Box sx={{ mt: 2, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                <Typography variant="caption" color="error.dark">
                  ⚠️ Sprint is at risk of not meeting goals
                </Typography>
              </Box>
            )}
            
            {metrics.hasBlockedIssues && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="caption" color="warning.dark">
                  🚫 {metrics.blockedIssues} blocked issue(s) need attention
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>

      {/* Timeline Dialog */}
      <Dialog 
        open={timelineOpen} 
        onClose={() => setTimelineOpen(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {sprint?.name || 'Sprint'} - Timeline View
            </Typography>
            <Button onClick={() => setTimelineOpen(false)} size="small">Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {sprint && sprintDetails && (
            <SprintTimelineView 
              sprint={sprintDetails}
              issues={sprintDetails.issues || []}
              height={600}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SprintMetricsCard;
